import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconCut } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import VideoDropZone from '../../components/video/VideoDropZone';
import useVideoPaste from '../../components/video/useVideoPaste';
import { VideoFileRow } from '../../components/video/VideoFileRow';
import VideoRangeSlider from '../../components/video/VideoRangeSlider';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ToolCard from '../../components/common/ToolCard';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import {
    MAX_TRIM_VIDEO_BYTES,
    MIN_TRIM_GAP_SECONDS,
    formatFileSize,
    formatVideoTime,
    getVideoDuration,
    isSupportedVideoFile,
    trimVideo,
    trimmedVideoName,
    preloadFFmpeg,
} from './trimVideoEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

export default function TrimVideo() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    const [file, setFile] = useState(null);
    const [duration, setDuration] = useState(0);
    const [trimRange, setTrimRange] = useState([0, 0]);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const videoPreviewUrl = useMemo(
        () => (file ? URL.createObjectURL(file) : ''),
        [file],
    );

    const resultPreviewUrl = useMemo(
        () => (result ? URL.createObjectURL(result.blob) : ''),
        [result],
    );

    useEffect(() => () => {
        if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    }, [videoPreviewUrl]);

    useEffect(() => () => {
        if (resultPreviewUrl) URL.revokeObjectURL(resultPreviewUrl);
    }, [resultPreviewUrl]);

    const trimStart = trimRange[0];
    const trimEnd = trimRange[1];
    const clipDuration = Math.max(0, trimEnd - trimStart);
    const canTrim = !!file && duration > 0 && clipDuration > 0;

    useEffect(() => {
        preloadFFmpeg();
    }, []);

    // Fallback when <video> metadata is slow (same duration source as trimVideo engine).
    useEffect(() => {
        if (!file || duration > 0) return;
        let cancelled = false;
        getVideoDuration(file).then((dur) => {
            if (!cancelled && dur > 0) {
                setDuration(dur);
                setTrimRange([0, dur]);
            }
        });
        return () => { cancelled = true; };
    }, [file, duration]);

    const addFile = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const videos = list.filter(isSupportedVideoFile);
        if (!videos.length) {
            const msg = t('trimVideo.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        const next = videos[0];
        if (next.size > MAX_TRIM_VIDEO_BYTES) {
            const msg = t('trimVideo.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setFile(next);
        setResult(null);
        setError(null);
        setProgress(null);
        setDuration(0);
        setTrimRange([0, 0]);
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page, the same as the image and PDF
    // tools. Ignored while a run is in flight: the clip would be dropped
    // on the floor by the reset that follows it.
    useVideoPaste(addFile, { enabled: !processing });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFile(e.dataTransfer.files);
    };

    const handleReset = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setProgress(null);
        setDuration(0);
        setTrimRange([0, 0]);
    };

    const handleVideoLoaded = useCallback((e) => {
        const dur = e.target.duration;
        if (dur && Number.isFinite(dur)) {
            setDuration(dur);
            setTrimRange([0, dur]);
        }
    }, []);

    const handleTrimChange = useCallback((range, thumb = 'start') => {
        setTrimRange(range);
        if (videoRef.current) {
            videoRef.current.currentTime = thumb === 'end' ? range[1] : range[0];
        }
    }, []);

    const resolveErrorMessage = (err) => {
        const code = err?.message;
        if (code === 'INVALID_RANGE') return t('trimVideo.errors.invalidRange');
        if (code === 'OUT_OF_MEMORY') return t('trimVideo.errors.outOfMemory');
        if (code === 'TIMEOUT') return t('trimVideo.errors.timeout');
        if (code === 'FFMPEG_NOT_LOADED') return t('trimVideo.errors.engine');
        if (code === 'ENCODE_FAILED' || code === 'EMPTY_OUTPUT' || code === 'TRIM_FAILED') {
            return t('trimVideo.errors.failed');
        }
        return t('trimVideo.errors.failed');
    };

    const handleTrim = async () => {
        if (!file || trimRange[1] <= trimRange[0] || processing) return;

        recordToolUsage('video/trim', { category: 'video' });

        setProcessing(true);
        setResult(null);
        setError(null);
        setProgress(0);

        try {
            const blob = await trimVideo(
                file,
                trimRange[0],
                trimRange[1],
                (value) => setProgress(value),
            );
            const output = {
                name: trimmedVideoName(file.name),
                originalSize: file.size,
                trimmedSize: blob.size,
                clipDuration,
                blob,
            };
            setResult(output);
            notifications.show({
                title: t('common.success'),
                message: t('trimVideo.done'),
                color: 'green',
            });
        } catch (err) {
            const msg = resolveErrorMessage(err);
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: canTrim,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`trimVideo.howTo.step${i}`)),
        [t],
    );

    const sizeNote = result ? (
        <>
            <span className="font-semibold text-[#3D3833] dark:text-zinc-200">
                {formatFileSize(result.originalSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">→</span>
            <span className="font-semibold text-[#1E9E5E]">
                {formatFileSize(result.trimmedSize)}
            </span>
            <span className="text-[#A8A099] dark:text-zinc-500">
                ({t('trimVideo.clipLength', { time: toBnDigits(formatVideoTime(result.clipDuration)) })})
            </span>
        </>
    ) : null;

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('trimVideo.done'),
    });

    const resultDownloads = result ? [{
        id: result.name,
        name: result.name,
        originalSize: formatFileSize(result.originalSize),
        compressedSize: formatFileSize(result.trimmedSize),
        onDownload: () => downloadBlob(result.blob, result.name),
    }] : undefined;

    return (
        <ToolPageLayout
            slug="video/trim"
            subtitleKey="trimVideo.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={false}
            howToSteps={howToSteps}
            onReset={file || result || error ? handleReset : undefined}
            primaryAction={{
                label: t('trimVideo.trim'),
                onClick: handleTrim,
                disabled: processing || duration <= 0 || clipDuration <= 0 || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,.mp4,.mov,.avi,.mkv,.webm,.m4v"
                        className="hidden"
                        onChange={(e) => { addFile(e.target.files); e.target.value = ''; }}
                    />

                    {!file ? (
                        <VideoDropZone
                            title={t('trimVideo.dropTitle')}
                            hint={t('trimVideo.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <VideoFileRow
                            name={file.name}
                            meta={formatFileSize(file.size)}
                            onRemove={handleReset}
                            removeLabel={t('trimVideo.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file && duration > 0 && !result ? (
                <ToolCard title={t('trimVideo.timelineTitle')}>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                <IconCut size={16} stroke={1.8} className="text-violet-500" />
                                {t('trimVideo.timelineLabel')}
                            </div>
                            <span className="rounded-full bg-violet-50 px-2.5 py-1 text-[11.5px] font-semibold text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
                                {t('trimVideo.selectedDuration', { time: toBnDigits(formatVideoTime(clipDuration)) })}
                            </span>
                        </div>

                        <VideoRangeSlider
                            min={0}
                            max={duration}
                            step={0.1}
                            minRange={MIN_TRIM_GAP_SECONDS}
                            value={trimRange}
                            onChange={handleTrimChange}
                            startLabel={t('trimVideo.startLabel', {
                                time: toBnDigits(formatVideoTime(trimStart)),
                            })}
                            endLabel={t('trimVideo.endLabel', {
                                time: toBnDigits(formatVideoTime(trimEnd)),
                            })}
                        />

                        <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('trimVideo.timelineHint', { time: toBnDigits(formatVideoTime(duration)) })}
                        </p>
                    </div>
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('trimVideo.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('trimVideo.previewEmpty')}
                    idleHint={t('trimVideo.previewEmptyHint')}
                    processingLabel={t('trimVideo.trimming')}
                    progress={progress ?? undefined}
                    error={error}
                    successLabel={t('trimVideo.done')}
                    downloadLabel={t('trimVideo.download')}
                    downloads={resultDownloads}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onAgain={result ? handleReset : undefined}
                    againLabel={t('trimVideo.startOver')}
                    sizeNote={sizeNote}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('trimVideo.trim')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                >
                    {file && !error ? (
                        <div className="flex h-full min-h-[280px] flex-col">
                            <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl border border-[#ECE8E4] bg-black dark:border-zinc-700">
                                <video
                                    ref={videoRef}
                                    key={result ? 'result' : videoPreviewUrl}
                                    src={result ? resultPreviewUrl : videoPreviewUrl}
                                    controls
                                    preload="metadata"
                                    onLoadedMetadata={result ? undefined : handleVideoLoaded}
                                    className="max-h-[360px] w-full max-w-full"
                                />
                            </div>
                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                                <span className="truncate font-medium text-[#3D3833] dark:text-zinc-300">
                                    {result ? result.name : file.name}
                                </span>
                                <span>
                                    {result
                                        ? toBnDigits(formatVideoTime(result.clipDuration))
                                        : duration > 0
                                            ? toBnDigits(formatVideoTime(duration))
                                            : formatFileSize(file.size)}
                                </span>
                            </div>
                        </div>
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
